/*
 * ===========================================================================
 * [StrongGate Limited] [http://www.stronggate.com]
 * Copyright (c) 2026. All rights Reserved.
 * Unauthorized access, use, dissemination, distribution, reproduction of this
 * file, or a portion of, in any form is prohibited and unlawful. If you receive
 * this file, and you are not an authorized company employee, or you believe you
 * have received this file in error, please notify the sender (or originator)
 * and promptly delete this file.
 *
 * Project:
 * File: index.js
 * Created by: chan.ying
 * Create Date: 2026-04-27 15:56
 * Version Number: 1.0
 * Version Description:
 *
 * =============================================================================
 */

import {createWebHashHistory, createRouter} from "vue-router";

import routes from './routes'
import OnlineUserManager from "../security/OnlineUserManager";
import {OperatesToScopesEnum} from "../components/OperatesToScopesEnum";
import {RouterPathDefineEnum} from "./RouterDefine";
import PageHelper from "../services/PageHelper";

const router = createRouter({
    history: createWebHashHistory("/"),
    routes,
    // Simulate native-like scroll behavior when navigating to a new
    // route and using back/forward buttons.
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition
        } else {
            return {left: 0, top: 0}
        }
    },
})

// The mapping relationship between routing paths and operation permissions
const operateMap = {
    [RouterPathDefineEnum.UserAdd]: OperatesToScopesEnum.AddUser,
    [RouterPathDefineEnum.UserEdit]: OperatesToScopesEnum.EditUser,

    [RouterPathDefineEnum.GroupAdd]: OperatesToScopesEnum.AddGroup,
    [RouterPathDefineEnum.GroupEdit]: OperatesToScopesEnum.EditGroup,

    [RouterPathDefineEnum.DevReqAdd]: OperatesToScopesEnum.AddResources,
    [RouterPathDefineEnum.DevReqEdit]: OperatesToScopesEnum.EditResources,

    [RouterPathDefineEnum.ResourceAdd]: OperatesToScopesEnum.AddResources,
    [RouterPathDefineEnum.ResourceEdit]: OperatesToScopesEnum.EditResources,

    [RouterPathDefineEnum.SecureNetWorkAdd]: OperatesToScopesEnum.AddResources,
    [RouterPathDefineEnum.SecureNetWorkEdit]: OperatesToScopesEnum.EditResources,

    [RouterPathDefineEnum.SavedPolicyAdd]: OperatesToScopesEnum.AddResources,
    [RouterPathDefineEnum.SavedPolicyEdit]: OperatesToScopesEnum.EditResources,

    [RouterPathDefineEnum.GeoProxyPolicyAdd]: OperatesToScopesEnum.AddResources,
    [RouterPathDefineEnum.GeoProxyPolicyEdit]: OperatesToScopesEnum.EditResources,

    [RouterPathDefineEnum.ResourceSectionAdd]: OperatesToScopesEnum.AddResources,
    [RouterPathDefineEnum.ResourceSectionEdit]: OperatesToScopesEnum.EditResources,

    [RouterPathDefineEnum.LocationAdd]: OperatesToScopesEnum.AddResources,
    [RouterPathDefineEnum.LocationEdit]: OperatesToScopesEnum.EditResources,

    [RouterPathDefineEnum.AccessPolicyAdd]: OperatesToScopesEnum.AddResources,
    [RouterPathDefineEnum.AccessPolicyEdit]: OperatesToScopesEnum.EditResources,

    [RouterPathDefineEnum.IpMacListAdd]: OperatesToScopesEnum.AddResources,
    [RouterPathDefineEnum.IpMacListEdit]: OperatesToScopesEnum.EditResources,
    [RouterPathDefineEnum.DeviceListAdd]: OperatesToScopesEnum.AddResources,
    [RouterPathDefineEnum.DeviceListEdit]: OperatesToScopesEnum.EditResources,
    [RouterPathDefineEnum.LoginListAdd]: OperatesToScopesEnum.AddResources,
    [RouterPathDefineEnum.LoginListEdit]: OperatesToScopesEnum.EditResources,

    [RouterPathDefineEnum.UGateAdd]: OperatesToScopesEnum.AddResources,
    [RouterPathDefineEnum.UGateEdit]: OperatesToScopesEnum.EditResources,

    [RouterPathDefineEnum.ConnectorAdd]: OperatesToScopesEnum.AddResources,
    [RouterPathDefineEnum.ConnectorEdit]: OperatesToScopesEnum.EditResources,

    [RouterPathDefineEnum.ClientApplicationAdd]: OperatesToScopesEnum.AddResources,
    [RouterPathDefineEnum.ClientApplicationEdit]: OperatesToScopesEnum.EditResources,
};

// Route Guard: Login check + Operation permission verification
router.beforeEach((to, from, next) => {

    // Routes explicitly marked as public (403/404/500, ...) must stay reachable
    // for unauthenticated users — otherwise showing an error page triggers a
    // logout-redirect loop.
    if (to.meta.authRequired === false) {
        next();
        return;
    }

    // Check whether the user has logged in
    if (!OnlineUserManager.Instance.get().userInfo) {
        PageHelper.Instance.goToLogout()
        return;
    }

    // Obtain the list of operation permissions owned by the user
    const availableOperates = OnlineUserManager.Instance.get().userInfo.operatesToScopes

    // The operation permissions required to search for the current route
    const requiredOp = operateMap[to.path];

    // If the routing requires permissions and the user does not have them, then redirect to the 403 page.
    if (requiredOp && !availableOperates.includes(requiredOp)) {
        next('/403');
        return;
    }

    next();
});

export default router
